import Reports from './pages/Reports';
import { AggregationPeriod } from '@queries/globalTypes';
import { CAMPAIGNS_EXPORT_QUERY } from '@queries/exportQueries/campaignsExport';
import { STORIES_EXPORT_QUERY } from '@queries/exportQueries/storiesExport';
import Authentication from '@services/Authentication';
import format from 'date-fns/format';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import addDays from 'date-fns/add_days';
import startOfMonth from 'date-fns/start_of_month';
import endOfDay from 'date-fns/end_of_day';
import fileDownload from 'js-file-download';
import { matchRouteToUseCase } from '@utils/transformUseCase';
import { CREATIVES_EXPORT_QUERY } from '@queries/exportQueries/creativesExport';
import { trackEvent } from '@services/MixPanel';
import { API_URL } from '../config';

function stringifyGQLQuery(query: any) {
  return query.loc.source.body;
}

function monthToYesterdayRange() {
  const today = new Date();
  const yesterday = addDays(today, -1);
  // On the 1st of the month yesterday belongs to the previous month, which would invert the range - export that whole month instead.
  const firstDay = yesterday < startOfMonth(today) ? startOfMonth(yesterday) : startOfMonth(today);
  return {
    startDate: format(firstDay),
    endDate: format(endOfDay(yesterday))
  };
}

type ExportConfiguration =
  | {
      type: 'campaigns';
      level: 'by_day';
    }
  | {
      type: 'campaigns';
      level: 'by_week';
    }
  | {
      type: 'campaigns';
      level: 'total';
    }
  | {
      type: 'stories';
      level: 'by_day';
    }
  | {
      type: 'stories';
      level: 'by_week';
    }
  | {
      type: 'stories';
      level: 'total';
    }
  | {
      type: 'stories';
      level: 'month_to_yesterday';
    }
  | {
      type: 'creatives';
      level: 'total';
    };

type DateRange = {
  startDate: string;
  endDate: string;
};

type GetCSV = {
  configuration: ExportConfiguration;
  startCb: () => void;
  successCb: () => void;
  failureCb: () => void;
};

class Export {
  _rootStore: Reports;

  private getQuery = (type: string) => {
    switch (type) {
      case 'campaigns':
        return stringifyGQLQuery(CAMPAIGNS_EXPORT_QUERY);
      case 'stories':
        return stringifyGQLQuery(STORIES_EXPORT_QUERY);
      case 'creatives':
        return stringifyGQLQuery(CREATIVES_EXPORT_QUERY);
      default:
        throw new Error('Invalid type for Export');
    }
  };

  private getAggregationPeriod = (level: string) => {
    switch (level) {
      case 'by_day':
        return AggregationPeriod.DAY;
      case 'by_week':
        return AggregationPeriod.SEVEN_DAYS;
      case 'total':
      case 'month_to_yesterday':
        return AggregationPeriod.TOTAL;
      default:
        throw new Error('Invalid level for Export');
    }
  };

  private getOperationName = (type: string) => {
    switch (type) {
      case 'campaigns':
        return 'CampaignsExportQuery';
      case 'stories':
        return 'StoriesExportQuery';
      case 'creatives':
        return 'CreativesExportQuery';
      default:
        throw new Error('Invalid type for Export');
    }
  };

  constructor(rootStore: Reports) {
    this._rootStore = rootStore;
  }

  private getDateRange = (configuration: ExportConfiguration): DateRange => {
    return configuration.level === 'month_to_yesterday'
      ? monthToYesterdayRange()
      : this._rootStore.datepicker.value.dateRange;
  };

  private getVariables = (configuration: ExportConfiguration, dateRange: DateRange) => {
    const currentUseCase = this._rootStore._rootStore.name;
    if (configuration.type === 'campaigns' || configuration.type === 'stories') {
      return {
        id: this._rootStore._rootStore._rootStore.userStore.publisher?.id ?? null,
        dateRange,
        conversionIds: this._rootStore.conversionIds,
        campaignTypes: this._rootStore.campaignTypeFilter.value,
        campaignIds: this._rootStore.campaignSelection.value,
        storyIds: this._rootStore.storySelection.value,
        aggregationPeriod: this.getAggregationPeriod(configuration.level),
        attributionWindow: this._rootStore.attributionWindow.value,
        useCases: [matchRouteToUseCase(currentUseCase)],
        ignoreRevenue: currentUseCase !== 'ads'
      };
    } else {
      return {
        id: this._rootStore._rootStore._rootStore.userStore.publisher?.id ?? null,
        storyIds: this._rootStore.storySelection.value,
        dateRange,
        conversionIds: this._rootStore.conversionIds,
        campaignTypes: this._rootStore.campaignTypeFilter.value,
        campaignIds: this._rootStore.campaignSelection.value,
        attributionWindow: this._rootStore.attributionWindow.value,
        useCases: [matchRouteToUseCase(this._rootStore._rootStore.name)]
      };
    }
  };

  getCSV = ({ configuration, startCb, successCb, failureCb }: GetCSV) => {
    const dateRange = this.getDateRange(configuration);
    trackEvent('export_csv', {
      publisherId: this._rootStore._rootStore._rootStore.userStore.publisher?.id,
      publisherName: this._rootStore._rootStore._rootStore.userStore.publisher?.name,
      type: configuration.type,
      aggregation: configuration.level,
      start: format(dateRange.startDate, 'YYYY-MM-DD'),
      end: format(dateRange.endDate, 'YYYY-MM-DD'),
      totalDays: differenceInCalendarDays(dateRange.startDate, dateRange.endDate)
    });
    startCb();
    let filename = '';
    fetch(`${API_URL}/graphql/reports`, {
      method: 'POST',
      headers: {
        'X-Access-Token': Authentication.getToken()!,
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        query: this.getQuery(configuration.type),
        variables: this.getVariables(configuration, dateRange),
        operationName: this.getOperationName(configuration.type),
        hierarchy: configuration.type,
        level: configuration.level,
        from_date: format(dateRange.startDate, 'YYYY-MM-DD'),
        to_date: format(dateRange.endDate, 'YYYY-MM-DD')
      })
    })
      .then(res => {
        if (res.status === 200 && res.headers.get('Content-Type') === 'text/csv') {
          const header = res.headers.get('Content-Disposition') || '';
          const fileNameMatch = header.match(/filename="(.+)"/);
          filename = fileNameMatch && fileNameMatch.length > 0 ? fileNameMatch[1] : '';
          return res.blob();
        }
        return;
      })
      .then(result => {
        if (result) {
          successCb();
          fileDownload(result, filename);
        }
      })
      .catch(() => {
        failureCb();
      });
  };
}

export default Export;
